import { getFunctions, httpsCallable } from 'firebase/functions';
import errorHandler from '../handlers/ErrorHandler.js';

/**
 * CloudFunctionsClient - Centralized Cloud Functions calling logic
 * 
 * Provides a unified interface for calling Firebase Cloud Functions
 * with consistent error handling, logging, and future extensibility
 * for features like retry logic, rate limiting, and request tracing.
 */
class CloudFunctionsClient {
    /**
     * Call a Firebase Cloud Function
     * 
     * @param {string} functionName - Name of the Cloud Function to call
     * @param {Object} data - Data to pass to the function
     * @returns {Promise<any>} Result data from the Cloud Function
     * @throws {Error} If the function call fails
     */
    static async call(functionName, data = {}) {
        try {
            // Validate parameters
            if (!functionName || typeof functionName !== 'string') {
                throw new Error('functionName is required and must be a string');
            }
            if (data !== null && typeof data !== 'object') {
                throw new Error('data must be an object');
            }

            const functions = getFunctions();
            const callable = httpsCallable(functions, functionName);
            const result = await callable(data);
            return result.data;
        } catch (error) {
            const errorMessage = `Cloud Function '${functionName}' failed: ${error.message}`;
            errorHandler.handle(error, { 
                operation: 'callCloudFunction',
                functionName, 
                data 
            }, 'error');
            throw new Error(errorMessage);
        }
    }
}

export default CloudFunctionsClient;
